import axios from "axios";
import { getHeaderToken } from "../utils/serviceHelper";
import { getCurrentSessionIdToken } from "../queries/AuthToken";
import { Asset } from "../types/asset";
import { errorToast, successToast } from "@idwal/idwal-react-components";
import { AdjustedGrade } from "../types/inspection";

export const getInspection = async (inspectionId: string) => {    
    const url = process.env.REACT_APP_IDWAL_API_ENDPOINT + `/Internal/Inspections/${inspectionId}?include=includeGrades`;

    const headers = await getHeaderToken();

    try {
        const response = await axios.get(url, { headers });

        if (response.data) {
            return response.data;
        }
    } catch (error) {
        console.warn(`Failed to get inspection: ${inspectionId}`);
    }
}

export const getInspectionAssetsByElementIds = async (inspectionId: string, elementIds: number[]) =>  {
    const url = `${process.env.REACT_APP_ASIMS_API_URL}/api/search?inspection-id=${inspectionId}`

    const token = await getCurrentSessionIdToken();

    const headers = {
        "Content-Type": "application/json",
        "idToken": token,
    }

    try {
        const promises = elementIds.map((elementId) =>
            axios.get(`${url}&element-id=${elementId}`, { headers })
        );

        const responses = await Promise.all(promises);

        let data: Asset[] = [];

        responses.forEach((response) => {
            if (response.data.data) {
                data = data.concat(response.data.data);
            }
        })

        return data;
    } catch (error) {
        console.warn(`Failed to get assets for inspection: ${inspectionId}`);
    }
}

export const getAssetsByIds = async (assetIds: string[], _inspectionId?: string) => {
    // Use direct asset endpoint - doesn't require inspection-id matching
    const url = `${process.env.REACT_APP_ASIMS_API_URL}/api/asset`

    const token = await getCurrentSessionIdToken();

    const headers = {
        "Content-Type": "application/json",
        "idToken": token,
    }

    try {
        // Fetch each asset directly by ID
        const promises = assetIds.map((assetId) =>
            axios.get(`${url}?assetId=${assetId}`, { headers }).catch(() => null)
        );

        const responses = await Promise.all(promises);

        const data: Asset[] = [];

        responses.forEach((response) => {
            if (response?.data?.data) {
                data.push(response.data.data);
            }
        })

        return data;
    } catch (error) {
        console.warn(`Failed to get assets by IDs`);
        return [];
    }
}

export const getInspectionAdjustedGrades = async (inspectionId: string) => {
    const url = process.env.REACT_APP_IDWAL_INTERNAL_API + `/inspections/${inspectionId}/grades/adjusted-grade?calculate=true`;

    const headers = {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_IDWAL_INTERNAL_API_KEY
    };

    try {
        const response = await axios.get(url, { headers });
        return response.data ?? null;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        errorToast("Failed to retrieve adjusted grades.");
    }
}

export const doUpdateInspectionAdjustedGrades = async (inspectionId: string, adjustedGrades: AdjustedGrade[]) => {
    const url = process.env.REACT_APP_IDWAL_INTERNAL_API + `/inspections/${inspectionId}/grades/adjusted-grade?calculate=true`;

    const headers = {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_IDWAL_INTERNAL_API_KEY
    };

    const body = {
        adjustedGrades: adjustedGrades
    }

    try {
        const response = await axios.post(url, body, { headers });

        if (response.status === 200) {
            successToast("Adjusted grades have been updated.");
            return response.data ?? null;
        }
    } catch (error) {
        errorToast("Failed to update adjusted grades.");
    }

    return null;
};

export const getRecalcualteAdjustedGrades = async (inspectionId: string, adjustedGrades: AdjustedGrade[]) => {
    const url = process.env.REACT_APP_IDWAL_INTERNAL_API + `/inspections/${inspectionId}/grades/adjusted-grade?calculate=true&inMemory=true`;

    const headers = {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_IDWAL_INTERNAL_API_KEY
    };

    const body = {
        adjustedGrades: adjustedGrades
    }

    try {
        const response = await axios.post(url, body, { headers });

        if (response.status === 200) {
            return response.data ?? null;
        }
    } catch (error) {
        errorToast("Failed to recalculate the adjusted grades.");
    }

    return null;
};

export const getAiSummaries = async (inspectionId: string) => {
    const url = process.env.REACT_APP_IDWAL_API_ENDPOINT + `/Inspection/getAugmentedSections/${inspectionId.toLowerCase()}`;

    const headers = await getHeaderToken();

    try {
        const response = await axios.get(url, { headers });
        return response.data ?? null;
    } catch (error: any) {
        if (error.response?.status === 500) {
            return null;
        }
    }

    return null;
};

export const saveAiSummaries = async (inspectionNumber: string, titleToKeyMap: Record<string, string>) => {
    const url = process.env.REACT_APP_IDWAL_API_ENDPOINT + `/Inspection/sendAugmentedSections`;

    const headers = await getHeaderToken();

    const conditionItems = Object.entries(titleToKeyMap).map(([conditionSummaryParagraphId, description]) => ({
        "ConditionSummaryParagraphId": conditionSummaryParagraphId,
        "Description": description
    }));

    const body = {
        "inspectionNumber": inspectionNumber,
        "conditionItems": conditionItems
    }

    await axios.post(url, body, { headers });

    return null;
};